import { useRef } from "react";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";
import { AuthImage } from "@/components/common/AuthImage";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Camera, Plus, Trash2, X } from "lucide-react";

export const newEditorItem = (text = "", photos = []) => ({
  key: Math.random().toString(36).slice(2),
  text,
  photos, // [{ kind: 'new', b64 } | { kind: 'existing', name, path }]
});

// Convert stored issue/RFI items into editor state. `photoPath(name)` builds
// the authenticated API path for an existing photo.
export const itemsToEditorState = (items, photoPath) =>
  (items || []).map((it) =>
    newEditorItem(
      it.text || "",
      (it.photos || []).map((name) => ({ kind: "existing", name, path: photoPath(name) })),
    ),
  );

// Convert editor state into the API payload shape.
export const editorItemsToPayload = (items) =>
  items
    .filter((it) => it.text.trim() || it.photos.length)
    .map((it) => ({
      text: it.text.trim(),
      keep_photos: it.photos.filter((p) => p.kind === "existing").map((p) => p.name),
      photos_b64: it.photos.filter((p) => p.kind === "new").map((p) => p.b64),
    }));

/** Multi-item + multi-photo editor shared by Quick Issues and RFIs. */
export const IssueItemsEditor = ({ items, setItems, itemNoun = "item", placeholder, testPrefix = "issue" }) => {
  const fileRef = useRef(null);
  const targetKeyRef = useRef(null);

  const pickPhotos = (key) => {
    targetKeyRef.current = key;
    fileRef.current?.click();
  };

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    e.target.value = "";
    if (!files.length) return;
    try {
      const b64s = await Promise.all(files.map((f) => compressImage(f)));
      setItems((prev) =>
        prev.map((it) =>
          it.key === targetKeyRef.current
            ? { ...it, photos: [...it.photos, ...b64s.map((b64) => ({ kind: "new", b64 }))].slice(0, 8) }
            : it,
        ),
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const setText = (key, text) => setItems((prev) => prev.map((it) => (it.key === key ? { ...it, text } : it)));
  const removeItem = (key) => setItems((prev) => (prev.length > 1 ? prev.filter((it) => it.key !== key) : prev));
  const removePhoto = (key, idx) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, photos: it.photos.filter((_, i) => i !== idx) } : it)));
  const addItem = () => setItems((prev) => [...prev, newEditorItem()]);

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-items-editor`}>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickPhotos} data-testid={`${testPrefix}-photo-input`} />
      {items.map((it, idx) => (
        <div key={it.key} className="rounded-xl border border-border bg-background p-3" data-testid={`${testPrefix}-item-${idx}`}>
          <div className="mb-2 flex items-center justify-between">
            <Label className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
              {itemNoun} {idx + 1}
            </Label>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(it.key)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                data-testid={`${testPrefix}-item-remove-${idx}`}
                aria-label={`Remove ${itemNoun} ${idx + 1}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          <Textarea
            value={it.text}
            onChange={(e) => setText(it.key, e.target.value)}
            placeholder={placeholder}
            rows={2}
            className="resize-none"
            data-testid={`${testPrefix}-item-text-${idx}`}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {it.photos.map((p, pIdx) => (
              <div key={`${it.key}-${pIdx}`} className="relative">
                {p.kind === "new" ? (
                  <img src={p.b64} alt={`${itemNoun} ${idx + 1} photo ${pIdx + 1}`} className="h-16 w-16 rounded-lg border border-border object-cover" data-testid={`${testPrefix}-item-${idx}-photo-${pIdx}`} />
                ) : (
                  <AuthImage path={p.path} className="h-16 w-16 rounded-lg border border-border" testId={`${testPrefix}-item-${idx}-photo-${pIdx}`} />
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(it.key, pIdx)}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow"
                  data-testid={`${testPrefix}-item-${idx}-photo-remove-${pIdx}`}
                  aria-label="Remove photo"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => pickPhotos(it.key)}
              className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-primary/50 bg-primary/5 text-primary transition-colors hover:bg-primary/10"
              data-testid={`${testPrefix}-item-add-photo-${idx}`}
              aria-label={`Add photos to ${itemNoun} ${idx + 1}`}
            >
              <Camera className="h-4 w-4" />
              <span className="text-[9px] font-semibold">Photos</span>
            </button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" className="h-10 w-full border-dashed text-xs font-semibold" onClick={addItem} data-testid={`${testPrefix}-add-item-btn`}>
        <Plus className="mr-1.5 h-4 w-4" /> Add another {itemNoun}
      </Button>
    </div>
  );
};
